/**
 * Settings -> Appearance: the logo the CRM header (and the login screen) shows,
 * plus the square mark that stands in for it while the sidebar is collapsed.
 *
 * Three cards in the CRM's own vocabulary: the wide logo, the collapsed mark,
 * then a live preview of the real header renderer in both states. The preview is
 * fed the draft rather than the stored value, so an operator sees the mark at the
 * height they just dragged to before they commit to it.
 */
import { useCallback, useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, RotateCcw, Save } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { hostSiteId, type StaffUser } from "../../../lib/auth";
import { MediaPicker } from "../../../components/MediaPicker";
import { BrandLogo } from "../../../components/BrandLogo";
import { useBranding } from "../../../components/BrandingProvider";
import { Btn, Card, CardHead } from "../../ui";
import { BrandingLogoSlot, type LogoKind, type LogoSlot } from "./BrandingLogoSlot";
import {
  getBranding, saveBranding, EMPTY_BRANDING, isDefaultBranding,
  CRM_LOGO_HEIGHT_MIN, CRM_LOGO_HEIGHT_MAX, CRM_LOGO_HEIGHT_DEFAULT,
  CRM_ICON_HEIGHT_MIN, CRM_ICON_HEIGHT_MAX, CRM_ICON_HEIGHT_DEFAULT,
  type CrmBranding,
} from "../../../lib/branding";

/** Which file the media picker is currently choosing for. */
type PickerTarget = `${LogoKind}.${LogoSlot}` | null;

/** The card body under a `CardHead`: `.crm-card` carries no padding of its own. */
const BODY = "px-6 pb-5 pt-3";

/**
 * A height in px. The range stays outside `.crm-inp`: that rule sets
 * `appearance: none` on any input inside it, which strips a range of its track
 * and its thumb.
 */
function HeightField({
  id, label, hint, value, min, max, fallback, onChange,
}: {
  id: string;
  label: string;
  hint: string;
  value: number;
  min: number;
  max: number;
  fallback: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="min-w-0">
      <label className="crm-lab !mt-0" htmlFor={id}>{label}</label>
      <div className="flex h-11 items-center gap-3">
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || fallback)}
          className="h-1.5 min-w-0 flex-1 cursor-pointer accent-[var(--color-primary)]"
        />
        <span className="crm-tnum w-14 shrink-0 text-right text-[13px] font-bold">{value} px</span>
      </div>
      <p className="crm-sub mt-2 text-[11.5px] leading-snug">{hint}</p>
    </div>
  );
}

export function BrandingSettings({ user }: { user: StaffUser }) {
  const t = useT();
  const siteId = hostSiteId(user);
  const { apply } = useBranding();

  const [draft, setDraft] = useState<CrmBranding>(EMPTY_BRANDING);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState<PickerTarget>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDraft({ ...EMPTY_BRANDING, ...(await getBranding()) });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  function edit(patch: Partial<CrmBranding>) {
    setDraft((d) => ({ ...d, ...patch }));
    setSaveState("idle");
  }

  /** The picked asset lands in the field the picker was opened for. */
  function applyPick(url: string) {
    if (picker === "logo.light") edit({ logoUrl: url });
    else if (picker === "logo.dark") edit({ logoDarkUrl: url });
    else if (picker === "icon.light") edit({ iconUrl: url });
    else if (picker === "icon.dark") edit({ iconDarkUrl: url });
  }

  async function save() {
    setSaveState("saving");
    try {
      const saved = await saveBranding(draft);
      setDraft(saved);
      // Push it into the context so the header above updates immediately.
      apply(saved);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20 text-[var(--color-muted-foreground)]">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  const usingDefault = isDefaultBranding(draft);
  const untouched =
    usingDefault && !draft.iconUrl && !draft.iconDarkUrl && !draft.alt &&
    draft.logoHeight === CRM_LOGO_HEIGHT_DEFAULT && draft.iconHeight === CRM_ICON_HEIGHT_DEFAULT;

  return (
    <div className="space-y-4">
      <Card>
        <CardHead title={t("settings.appearance.logoTitle")} sub={t("settings.appearance.logoDesc")} />
        <div className={BODY}>
          <div className="grid gap-6 sm:grid-cols-2">
            <BrandingLogoSlot
              kind="logo"
              slot="light"
              url={draft.logoUrl}
              height={draft.logoHeight}
              canPick={!!siteId}
              onPick={() => setPicker("logo.light")}
              onClear={() => edit({ logoUrl: "" })}
            />
            <BrandingLogoSlot
              kind="logo"
              slot="dark"
              url={draft.logoDarkUrl}
              height={draft.logoHeight}
              canPick={!!siteId}
              onPick={() => setPicker("logo.dark")}
              onClear={() => edit({ logoDarkUrl: "" })}
            />
          </div>

          <div className="mt-6 grid gap-x-6 gap-y-4 border-t border-[var(--crm-hair)] pt-5 sm:grid-cols-2">
            <HeightField
              id="logoHeight"
              label={t("settings.appearance.height")}
              hint={t("settings.appearance.heightHint")}
              value={draft.logoHeight}
              min={CRM_LOGO_HEIGHT_MIN}
              max={CRM_LOGO_HEIGHT_MAX}
              fallback={CRM_LOGO_HEIGHT_DEFAULT}
              onChange={(logoHeight) => edit({ logoHeight })}
            />

            {/* Accessible name - a screen reader must not announce "WebAdmin" over
                another brand's mark. `min-w-0` on the label, not only on the
                input: the track is sized from the label's intrinsic width. */}
            <label className="block min-w-0">
              <span className="crm-lab !mt-0">{t("settings.appearance.alt")}</span>
              <span className="crm-inp h-11">
                <input
                  value={draft.alt}
                  maxLength={60}
                  placeholder="WebAdmin"
                  onChange={(e) => edit({ alt: e.target.value })}
                />
              </span>
              <span className="crm-sub mt-2 block text-[11.5px] leading-snug">{t("settings.appearance.altHint")}</span>
            </label>
          </div>
        </div>
      </Card>

      {/* The collapsed rail is 58px wide: a wordmark does not fit in it, and the
          header used to simply cut one in half. This is the file that stands in
          for it there. */}
      <Card>
        <CardHead title={t("settings.appearance.iconTitle")} sub={t("settings.appearance.iconDesc")} />
        <div className={BODY}>
          <div className="grid gap-6 sm:grid-cols-2">
            <BrandingLogoSlot
              kind="icon"
              slot="light"
              url={draft.iconUrl}
              height={draft.iconHeight}
              canPick={!!siteId}
              onPick={() => setPicker("icon.light")}
              onClear={() => edit({ iconUrl: "" })}
            />
            <BrandingLogoSlot
              kind="icon"
              slot="dark"
              url={draft.iconDarkUrl}
              height={draft.iconHeight}
              canPick={!!siteId}
              onPick={() => setPicker("icon.dark")}
              onClear={() => edit({ iconDarkUrl: "" })}
            />
          </div>

          <div className="mt-6 grid gap-x-6 gap-y-4 border-t border-[var(--crm-hair)] pt-5 sm:grid-cols-2">
            <HeightField
              id="iconHeight"
              label={t("settings.appearance.iconHeight")}
              hint={t("settings.appearance.iconHeightHint")}
              value={draft.iconHeight}
              min={CRM_ICON_HEIGHT_MIN}
              max={CRM_ICON_HEIGHT_MAX}
              fallback={CRM_ICON_HEIGHT_DEFAULT}
              onChange={(iconHeight) => edit({ iconHeight })}
            />
          </div>
        </div>
      </Card>

      {/* The real header renderer, in the theme you are looking at. */}
      <Card>
        <CardHead title={t("settings.appearance.preview")} sub={t("settings.appearance.previewHint")} />
        <div className={BODY}>
          {/* Deliberately the same three things `shell/TopBar.tsx` puts in its
              brand block, at the same sizes: the header's own height variable,
              a 17px fallback mark, and `.crm-chip-brand`. The wrapper is not
              `.crm-brand` itself - that one is exactly as wide as the sidebar
              and would crop a wide logo here without cropping it in the header. */}
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <div className="overflow-hidden rounded-[24px] bg-[var(--crm-sunk)]">
              <div className="flex h-[var(--crm-header-h)] items-center gap-[11px] px-5">
                <BrandLogo className="h-[17px] w-auto text-foreground" branding={draft} />
                <span className="crm-chip-brand">{t("app.badge")}</span>
              </div>
              <p className="crm-sub px-5 pb-3 text-[11.5px]">{t("settings.appearance.previewWide")}</p>
            </div>

            {/* The collapsed state at its real width, so a mark that is too big
                for the rail is visible as too big here. */}
            <div className="overflow-hidden rounded-[24px] bg-[var(--crm-sunk)]">
              <div className="flex h-[var(--crm-header-h)] min-w-[86px] items-center justify-center px-4">
                <BrandLogo branding={draft} mark="icon" />
              </div>
              <p className="crm-sub px-4 pb-3 text-center text-[11.5px]">{t("settings.appearance.previewMini")}</p>
            </div>
          </div>
          {usingDefault ? <p className="crm-fnote">{t("settings.appearance.usingDefault")}</p> : null}
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-3 px-1">
        <Btn tone="dark" onClick={() => void save()} disabled={saveState === "saving"}>
          {saveState === "saving" ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          {t(saveState === "saving" ? "settings.appearance.saving" : "settings.appearance.save")}
        </Btn>
        <Btn onClick={() => { setDraft(EMPTY_BRANDING); setSaveState("idle"); }} disabled={untouched}>
          <RotateCcw className="size-4" />
          {t("settings.appearance.reset")}
        </Btn>
        {saveState === "saved" ? (
          <span className="flex items-center gap-1.5 text-[12.5px] font-[550] text-[var(--color-success)]">
            <CheckCircle2 className="size-4" />
            {t("settings.appearance.saved")}
          </span>
        ) : null}
        {saveState === "error" ? (
          <span className="flex items-center gap-1.5 text-[12.5px] font-[550] text-[var(--color-destructive)]">
            <AlertCircle className="size-4" />
            {t("settings.appearance.saveError")}
          </span>
        ) : null}
      </div>

      {siteId ? (
        <MediaPicker
          open={picker !== null}
          siteId={siteId}
          onClose={() => setPicker(null)}
          onPick={(asset) => applyPick(asset.url)}
        />
      ) : null}
    </div>
  );
}
